import {
	type ContextId,
	type ExtensionId,
	type EnvironmentType,
	type ForgeContextToken,
	type ForgeMetrics,
	type LicenseDetails,
	type UserAccessDetails,
} from '@atlassian/forge-ui-types';
import { type GraphQLError } from 'graphql';
import type { PayloadError } from 'relay-runtime';
export interface GQLUser {
	__typename?: 'GQLUser';
	accountId: string;
	name: string;
	picture: string;
}

interface InstallationConfig {
	key: string;
	value: boolean;
}

export interface GQLExtension {
	__typename?: 'GQLExtension';
	sandboxPolicies?: string[];
	// Marking as optional to not introduce a breaking type change into Confluence
	appId?: string;
	appOwner?: GQLUser;
	appVersion?: string;
	consentUrl?: string;
	currentUserConsent?: {
		appEnvironmentVersion: {
			id: string;
		};
		consentedAt: string;
		user: {
			aaid: GQLUser['accountId'];
		};
	};
	dataClassificationPolicyDecision?: {
		status: string;
	};
	egress?: {
		addresses: string[];
		category?: string;
		inScopeEUD?: boolean;
		type: string;
	}[];
	environmentId: string;
	environmentKey: string;
	environmentType: EnvironmentType;
	hiddenBy?: 'APP_ACCESS_RULES' | 'DISPLAY_CONDITIONS';
	id: string; // ari:cloud:ecosystem::extension/<appId>/<environmentId>/static/<key>
	installationConfig?: InstallationConfig[];
	installationId: string;
	key?: string;
	// Marking as optional to not introduce a breaking type change into Confluence
	license?: LicenseDetails | null;
	migrationKey?: string;
	oauthClientId?: string;
	properties: any;
	requiresUserConsent?: boolean;
	scopes?: string[];
	type: string;
	userAccess?: UserAccessDetails | null;
}

export interface GQLExtensionContextExtensionsByTypeVariables {
	locale?: string | null;
	type: string;
}

export interface GQLExtensionContextExtensionsVariables {
	filter: GQLExtensionContextsFilter[];
	locale?: string | null;
}

export interface GQLExtensionContextsVariables {
	contextIds: ContextId[];
	locale?: string | null;
}

export enum GQLExtensionContextsFilterType {
	Container = 'CONTAINER',
	DataClassificationTag = 'DATA_CLASSIFICATION_TAG',
	ExtensionType = 'EXTENSION_TYPE',
	PrincipalType = 'PRINCIPAL_TYPE',
	AppIdEnvironmentId = 'APP_ID_ENVIRONMENT_ID',
}
export interface GQLExtensionContextsFilter {
	type: GQLExtensionContextsFilterType;
	value: string[] | string;
}

export interface GQLExtensionContextOld<TExtension = GQLExtension> {
	__typename?: 'GQLExtensionContext';
	extensionsByType: TExtension[];
	id: string;
}
export interface GQLExtensionContext<TExtension = GQLExtension> {
	__typename?: 'GQLExtensionContext';
	extensions: TExtension[];
	id: string;
}

export interface GQLCustomUITunnelDefinition {
	resourceKey: string;
	tunnelUrl: string;
}

export interface GQLAppTunnels {
	__typename?: 'GQLAppTunnels';
	customUI: GQLCustomUITunnelDefinition[];
	faasTunnelUrl: string;
}

export interface GQLMutationResponse {
	errors: GQLMutationError[] | null;
	success: boolean;
}

export interface GQLMutationError<Fields = unknown> {
	extensions: {
		errorType: string;
		fields?: Fields;
		statusCode: number;
	};
	message: string;
}

export interface GQLEffect {
	[key: string]: any;
	type: string;
}

export interface GQLAuxEffectsInvocationPayload {
	config?: object;
	context: object;
	contextToken?: string;
	effects: GQLEffect[];
	state: object;
}

export interface GQLInvokeAuxEffectsInput {
	contextIds: ContextId[];
	extensionId: ExtensionId;
	function?: string;
	payload: GQLAuxEffectsInvocationPayload;
}

export interface GQLAuxEffectsResult {
	__typename?: 'GQLAuxEffectsResult';
	contextToken?: ForgeContextToken;
	effects: GQLEffect[];
	metrics?: ForgeMetrics;
}

export interface GQLInvokeAuxEffectsResponse extends GQLMutationResponse {
	result?: GQLAuxEffectsResult;
}

export interface GQLInvokeExtensionPayload {
	call: {
		functionKey: string;
		payload: object;
	};
	context: {
		[key: string]: any;
	};
	contextToken?: string;
	exportType?: string;
	extensionPayload?: {
		[key: string]: any;
	};
}

export type GQLInvokeExtensionEntrypoint = 'resolver' | 'adfExport';

export interface GQLInvokeExtensionInput {
	contextIds: ContextId[];
	entryPoint: GQLInvokeExtensionEntrypoint;
	extensionId: ExtensionId;
	payload: GQLInvokeExtensionPayload;
}

export interface GQLForgeRateLimitProperties {
	rateLimitRemaining: number | null;
	rateLimitReset: number | null;
	rateLimitValue: number | null;
}

export interface GQLInvokeExtensionResponse extends GQLMutationResponse {
	contextToken?: ForgeContextToken;
	errors: Array<GQLMutationError<{ authInfoUrl?: string }>>;
	rateLimitProperties?: GQLForgeRateLimitProperties | null;
	response: {
		body: any;
	};
	success: boolean;
}

export interface GQLUnderlyingServiceError extends GraphQLError {
	extensions: {
		errorSource: 'UNDERLYING_SERVICE';
	};
}

export interface GQLGatewayError extends GraphQLError {
	extensions: {
		errorSource: 'GRAPHQL_GATEWAY';
	};
}

type RelayGraphQLError = PayloadError & { extensions?: Record<string, any> };
export interface RelayNetworkError extends Error {
	name: 'RelayNetwork';
	source: {
		errors: ReadonlyArray<RelayGraphQLError>;
		operation: any;
		variables: Record<string, any>;
	};
}

export const isRelayNetworkError = (error: unknown): error is RelayNetworkError => {
	return (
		error instanceof Error &&
		error.name === 'RelayNetwork' &&
		'source' in error &&
		Array.isArray((error as any).source?.errors)
	);
};

export const isGQLGatewayError = (err: GraphQLError | RelayGraphQLError): err is GQLGatewayError =>
	err.extensions && err.extensions.errorSource && err.extensions.errorSource === 'GRAPHQL_GATEWAY';

export const isGQLUnderlyingServiceError = (
	err: GraphQLError | RelayGraphQLError,
): err is GQLUnderlyingServiceError =>
	err.extensions &&
	err.extensions.errorSource &&
	err.extensions.errorSource === 'UNDERLYING_SERVICE';

export type { GQLExtension as Extension, GQLExtensionContext as ExtensionContext };
